import { createContext, use, useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteSessionToken,
  getMe,
  getSessionToken,
  saveSessionToken,
  setUnauthorizedHandler,
} from "../utils";
import { loadSession } from "./loadSession";

interface SessionContextValue {
  user: User | null;
  loading: boolean;
  loadError: Error | null;
  retry(): void;
  signIn(token: string): Promise<User>;
  replaceToken(token: string): void;
  signOut(): void;
}

export const SessionContext = createContext<SessionContextValue>({
  user: null,
  loading: true,
  loadError: null,
  retry: () => {},
  signIn: async () => {
    throw new Error("SessionProvider is missing");
  },
  replaceToken: () => {},
  signOut: () => {},
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(() => getSessionToken() != null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const signOut = useCallback(() => {
    deleteSessionToken();
    setLoadError(null);
    setUser(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await loadSession(getMe);
    if (result.status === "signedIn") {
      setUser(result.user);
    } else if (result.status === "signedOut") {
      signOut();
    } else {
      setLoadError(result.error);
    }
    setLoading(false);
  }, [signOut]);

  useEffect(() => {
    setUnauthorizedHandler(signOut);
    if (getSessionToken() != null) {
      load();
    }
  }, [signOut, load]);

  const signIn = useCallback(async (token: string) => {
    saveSessionToken(token);
    const me: User = await getMe();
    setLoadError(null);
    setUser(me);
    return me;
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      loadError,
      retry: load,
      signIn,
      replaceToken: saveSessionToken,
      signOut,
    }),
    [user, loading, loadError, load, signIn, signOut]
  );

  return <SessionContext value={value}>{children}</SessionContext>;
};

export const useSession = () => use(SessionContext);
